import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import { firmaApi, GenerarLinkData } from '../services/api.ts';

const GenerarLink: React.FC = () => {
  const [formData, setFormData] = useState<GenerarLinkData>({
    email: '',
    nombre: '',
    titulo: '',
    descripcion: '',
    verificacionId: '',
  });

  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResultado(null);

    try {
      const response = await firmaApi.generarLinkFirma(formData);
      setResultado(response);
      setShowLinkDialog(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al generar link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (resultado?.url) {
      navigator.clipboard.writeText(resultado.url);
      alert('Link copiado al portapapeles');
    }
  };

  const handleSendEmail = () => {
    if (resultado?.url && resultado?.email) {
      const subject = encodeURIComponent(`Firma de documento: ${resultado.titulo}`);
      const body = encodeURIComponent(`
Hola ${resultado.nombre},

Has recibido una solicitud para firmar el documento: "${resultado.titulo}"

Para proceder con la firma, haz clic en el siguiente enlace:
${resultado.url}

Este link expira en 7 días.

Saludos,
Sistema de Firma Electrónica
      `);
      
      window.open(`mailto:${resultado.email}?subject=${subject}&body=${body}`);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Generar Link de Firma
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Genera un link único que puedes enviar por email para que el firmante complete el proceso de firma electrónica.
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email del firmante"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del firmante"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título del documento"
                name="titulo"
                value={formData.titulo}
                onChange={handleInputChange}
                required
                placeholder="Ej: Contrato de Servicios"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción del documento"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
                multiline
                rows={3}
                placeholder="Breve descripción del documento a firmar"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ID de verificación (opcional)"
                name="verificacionId"
                value={formData.verificacionId}
                onChange={handleInputChange}
                placeholder="ID de verificación de identidad previa"
                helperText="Si ya completaste la verificación de identidad, incluye el ID aquí"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Generar Link de Firma'}
              </Button>
            </Grid>
          </Grid>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Diálogo con el link generado */}
      <Dialog open={showLinkDialog} onClose={() => setShowLinkDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Link de Firma Generado</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Link único para firma
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Firmante:</strong> {resultado?.nombre}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Email:</strong> {resultado?.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Documento:</strong> {resultado?.titulo}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Expira:</strong> {resultado?.fechaExpiracion ? new Date(resultado.fechaExpiracion).toLocaleDateString() : '7 días'}
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Link de firma"
            value={resultado?.url || ''}
            InputProps={{
              readOnly: true,
            }}
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Este link es único y expira en 7 días. Compártelo de forma segura con el firmante.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLinkDialog(false)}>
            Cerrar
          </Button>
          <Button onClick={handleCopyLink} variant="outlined">
            Copiar Link
          </Button>
          <Button onClick={handleSendEmail} variant="contained">
            Enviar por Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GenerarLink; 